/**
 * Black Friday 2026 (6 Nov 08:00 - 13 Nov 20:00). Campaign dates come from
 * the REACT_APP_CAMPAIGN_* env variables (utils/dateUtils), everything else
 * the shop needs to know about the campaign lives here.
 *
 * Spec: temp/black-friday/Black Friday 2026 (3)/BF2026_fejlesztoi_osszefoglalo.md
 * and the PM's answers of 2026-11-03 (same folder).
 */

/** Tree code of the BLACK FRIDAY category. tst28: #20000; PROD: same code
 * expected, but it is created separately, hence the env override. */
export const BF_ROOT_CATEGORY_CODE =
  process?.env?.REACT_APP_BF_CATEGORY_CODE || "#20000"

export const BF_ROOT_CATEGORY_NAME = "BLACK FRIDAY"

/** The 2025 campaign category, still present (hidden) on tst28. */
export const BF_2025_CATEGORY_CODE = "#27616"

export const BF_DATE_LABEL = "6 – 13 noiembrie 2026"
export const BF_SHORT_DATE_LABEL = "6–13 nov."

/** Countdown target on the banner and in the last-day popup. */
export const BF_END = new Date("2026-11-13T20:00:00+02:00")

/** Product list (PDF) sent by the client; the link stays hidden while empty.
 * PROD public assets bucket, same url on every environment; a corrected PDF
 * replaces the object under the same key (no rebuild needed). */
export const BF_PRODUCT_LIST_PDF_URL =
  "https://b2bawsinfrastack-assetsbuckete5c0e90f-1asbmp7pmqc41.s3.eu-central-1.amazonaws.com/media/black-friday-2026-lista-preturi.pdf"

/** dataLayer event for the PDF link (GTM trigger: Custom Event, same name). */
export const BF_PDF_CLICK_EVENT = "bf_pdf_list_click"

/**
 * The 5 sub-categories, in the order of the client PDF. Matched by the
 * catalog name (codes may differ between tst and PROD); a child of the BF
 * category that is not listed here (GAZ BF) is not offered anywhere.
 * `productCount` and `maxDiscount` are the client's numbers (2 Nov).
 */
export const BF_SUBCATEGORIES = [
  {
    catalogName: "AC, VENTILATIE BF",
    slug: "aer-ventilatie",
    title: "Aer & ventilație",
    chip: "Aer & ventilație",
    icon: "aer",
    blurb: "Sisteme profesionale de aer condiționat și ventilație",
    productCount: 57,
    maxDiscount: 97
  },
  {
    catalogName: "TERMICE, INCALZIRE BF",
    slug: "termice-incalzire",
    title: "Termice & încălzire",
    chip: "Termice",
    icon: "termice",
    blurb: "Cazane, centrale termice, boilere, radiatoare, pompe de căldură",
    productCount: 573,
    maxDiscount: 94
  },
  {
    catalogName: "APA BF",
    slug: "apa",
    title: "Apă",
    chip: "Apă",
    icon: "apa",
    blurb: "Filtre, sifoane, sisteme de țevi și fittinguri",
    productCount: 60,
    maxDiscount: 94
  },
  {
    catalogName: "SANITARE BF",
    slug: "sanitare",
    title: "Sanitare",
    chip: "Sanitare",
    icon: "sanitare",
    blurb: "Obiecte sanitare, baterii, rame WC",
    productCount: 109,
    maxDiscount: 78
  },
  {
    catalogName: "ENERGIE VERDE BF",
    slug: "energie-verde",
    title: "Energie verde",
    chip: "Energie verde",
    icon: "energie",
    blurb: "Accesorii pentru panouri fotovoltaice",
    productCount: 38,
    maxDiscount: 67
  }
]

const normalizeName = (name) => String(name || "").toUpperCase().trim()

export const getBfSubcategoryMeta = (catalogName) =>
  BF_SUBCATEGORIES.find((s) => s.catalogName === normalizeName(catalogName)) || null

/** True for the BF category itself and for any of its descendants. */
export const isBfCategoryCode = (treeCode) => {
  if (typeof treeCode !== "string" || !treeCode) return false
  if (treeCode === BF_ROOT_CATEGORY_CODE) return true
  return treeCode.startsWith(`${BF_ROOT_CATEGORY_CODE}#`)
}

/**
 * The BF sub-categories that exist in the loaded catalog
 * (state.backData.productCategory), in the configured order, each with its
 * tree code. Unknown children (GAZ BF) are left out.
 */
export const getBfSubcategories = (productCategories) => {
  const root = (productCategories || []).find(
    (c) => c?.treeCode === BF_ROOT_CATEGORY_CODE
  )
  const children = root?.childList || []
  return BF_SUBCATEGORIES.map((meta) => {
    const child = children.find((c) => normalizeName(c?.name) === meta.catalogName)
    return child ? { ...meta, treeCode: child.treeCode, id: child.id } : null
  }).filter(Boolean)
}

export const getBfListUrl = (treeCode = BF_ROOT_CATEGORY_CODE) =>
  `/shop/productlist?filters=${encodeURIComponent(
    JSON.stringify({ "FLT-CATEG001": [treeCode] })
  )}&page=1&sortBy=ranking`

/** Percentage between the list price and the Black Friday price, rounded
 * down like the client's own numbers (78.59% -> 78). Null when unknown. */
export const getBfDiscountPercent = (listPrice, bfPrice) => {
  const list = Number(listPrice)
  const price = Number(bfPrice)
  if (!(list > 0) || !(price > 0) || price >= list) return null
  const percent = Math.floor(((list - price) / list) * 100)
  return percent > 0 ? percent : null
}
